import React from 'react';
import styled from 'styled-components';
import _ from 'lodash';
import {useField} from 'formik';

import {CheckboxDropdownStructure} from './CheckboxDropdownStructure';
import {SingleCheckBox} from '../../form-v2/form-control-type/selection-controls/single-checkBox/SingleCheckBox';

/**
 * This component displays a list of selectable checkbox options.
 * It allows selecting one or more options. Connects to the closest upstream Formik
 * context, based on the specified `name`.
 *
 * @param {object} param0
 * Component props.
 *
 * @param {string} param0.name
 * The name that will be used as an identifier for the component. Should match the
 * form field name defined in the Formik context.
 *
 * @param {"top-center" | "top-start" | "top-end" | "left-start" | "left-center" | "left-end" | "right-start" | "right-center" | "right-end" | "bottom-start" | "bottom-center" | "bottom-end" | "center"} [placement]
 *
 * @param {Array<{id: any; name: any}>} param0.options
 * The list of options to display in the dropdown. Each option should have a unique
 * `id`. The `name` property is displayed as-is.
 *
 * @param {(close: () => void) => JSX.Element} param0.footer
 * A function that returns an element to be rendered in the dropdown footer.
 * The function provided as a parameter, closes the dropdown when invoked.
 *
 * @param {JSX.Element} param0.children
 * This component is rendered inside the dropdown toggle button.
 * It can be used to show the selected options, for example.
 */
function FormikCheckboxDropdown({options, name, placement, footer, children}) {
    const [, , helpers] = useField(name);

    const selectAll = () => {
        helpers.setValue(_.map(options, 'id'));
    };

    const deselectAll = () => {
        helpers.setValue([]);
    };

    return (
        <CheckboxDropdownStructure
            footer={footer}
            placement={placement}
            onSelectAll={selectAll}
            onDeselectAll={deselectAll}
            optionList={
                options &&
                options.map((attr) => {
                    return (
                        <Wrapper key={attr.id}>
                            <SingleCheckBox name={name} label={attr.name} value={attr.id} />
                        </Wrapper>
                    );
                })
            }>
            {children}
        </CheckboxDropdownStructure>
    );
}

export {FormikCheckboxDropdown};
const Wrapper = styled.div`
    ${SingleCheckBox.Wrapper} {
        margin: 0;
    }
`;
